/**
 * Firebase Cloud Messaging (FCM) Integration
 * Handles push notification setup, token management, and message handling
 */

import { getMessaging, getToken, deleteToken, onMessage } from 'firebase/messaging';
import { auth, db, COLLECTIONS } from '../core/firebase-config';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { showNotification } from './notifications';
import logger from '../core/logger';
import { isNative } from '../core/platform';
import analytics from '../analytics/analytics';

// FCM configuration
const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;
const RECORD_TAP_URL = import.meta.env.VITE_RECORD_TAP_URL;

let messaging = null;
let currentToken = null;
let messagingSwRegistration = null;

/**
 * Register the dedicated Firebase Messaging service worker
 * This must be separate from the PWA service-worker.js to avoid conflicts
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
async function registerMessagingSW() {
  if (messagingSwRegistration) return messagingSwRegistration;
  if (isNative()) return null; // Service workers don't run in WKWebView

  try {
    messagingSwRegistration = await navigator.serviceWorker.register(
      '/firebase-messaging-sw.js',
      { scope: '/firebase-cloud-messaging-push-scope' }
    );
    logger.info('[FCM] Messaging service worker registered');
    return messagingSwRegistration;
  } catch (error) {
    logger.error('[FCM] Failed to register messaging SW:', error);
    return null;
  }
}

/**
 * Initialize Firebase Cloud Messaging
 * @returns {Promise<void>}
 */
export async function initializeFCM() {
  try {
    // Service workers don't work in Capacitor native — use nativePush.js instead
    if (isNative()) return;

    // Check if FCM is supported
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      return;
    }

    // Register the dedicated messaging service worker first
    await registerMessagingSW();

    // Initialize messaging
    messaging = getMessaging();

    // Set up message handler for foreground notifications
    onMessage(messaging, (payload) => {
      handleForegroundMessage(payload);
    });

    logger.info('[FCM] Initialized successfully');

  } catch (error) {
    logger.error('[FCM] Initialization failed:', error);
  }
}

/**
 * Self-healing: if the given FCM token was previously marked invalid by the server
 * (messaging/invalid-argument — e.g. an APNs device token on the web FCM endpoint),
 * force-unregister the push subscription and get a brand-new one so the user
 * starts receiving notifications again.
 *
 * @param {string} token - the token just returned by getToken()
 * @returns {Promise<string|null>} fresh replacement token, or null if no healing needed / failed
 */
async function refreshIfServerInvalidated(token) {
  const user = auth.currentUser;
  if (!user) return null;

  const deviceId = token.slice(-32);
  try {
    const tokenRef = doc(db, COLLECTIONS.FCM_TOKENS, user.uid);
    const snap = await getDoc(tokenRef);
    if (!snap.exists()) return null;

    const deviceData = snap.data()?.devices?.[deviceId];
    if (!deviceData?.invalidatedAt) return null;

    // This token was previously rejected by the FCM API — the browser's push
    // subscription is stale. Delete it so the next getToken() creates a new one.
    logger.info('[FCM] Token was previously invalidated by server — refreshing push subscription');

    if (messaging) {
      try { await deleteToken(messaging); } catch (e) { /* ignore */ }
      messaging = null;
      messagingSwRegistration = null;
    }

    // Re-initialize and get a fresh token
    await initializeFCM();
    const newSwReg = await registerMessagingSW();
    if (!messaging || !newSwReg) return null;

    const freshToken = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: newSwReg
    });

    if (freshToken && freshToken !== token) {
      logger.info('[FCM] Self-heal: obtained fresh push subscription token');
      return freshToken;
    }

    // Browser returned the same token — push manager may have already re-registered.
    // Clear the invalidatedAt flag so we don't loop forever.
    logger.warn('[FCM] Self-heal: browser returned same token after refresh — clearing invalidation flag');
    return null;
  } catch (err) {
    logger.warn('[FCM] Self-heal check failed (non-fatal):', err.message);
    return null;
  }
}

/**
 * Request notification permission and get FCM token
 *
 * IMPORTANT: Notification.requestPermission() MUST be called as the very
 * first async operation so it stays within the browser's user-gesture window.
 * If we do async FCM init first, the browser silently blocks the prompt.
 *
 * @returns {Promise<string|null>} FCM token or null if permission denied
 */
export async function requestFCMToken() {
  // Native uses APNs via nativePush.js — web FCM token flow doesn't apply
  if (isNative()) return null;

  try {
    // 1. Check/request permission FIRST — must be the immediate async call
    //    after the user gesture (click/tap) or the browser will block it.
    if (Notification.permission === 'denied') {
      logger.warn('[FCM] Notification permission denied');
      return null;
    }

    if (Notification.permission !== 'granted') {
      const permission = await Notification.requestPermission();
      logger.info('[FCM] Permission prompt result:', permission);
      if (permission !== 'granted') {
        return null;
      }
    }

    // 2. Now that we have permission, initialize FCM (safe to do async work)
    if (!messaging) {
      await initializeFCM();
    }

    // Ensure messaging SW is registered before requesting token
    const swRegistration = await registerMessagingSW();

    if (!messaging || !swRegistration) {
      logger.error('[FCM] Messaging or service worker failed to initialize');
      return null;
    }

    // Get registration token with explicit SW registration
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swRegistration
    });

    if (token) {
      // Self-healing: if this exact token was previously invalidated by the server
      // (e.g. messaging/invalid-argument from a malformed push subscription), force-delete
      // the browser push subscription and get a fresh one so delivery starts working again.
      const healedToken = await refreshIfServerInvalidated(token);
      const finalToken = healedToken || token;

      currentToken = finalToken;
      await saveFCMToken(finalToken);
      return finalToken;
    } else {
      return null;
    }

  } catch (error) {
    logger.error('[FCM] Error getting token:', error);

    if (error.code === 'messaging/permission-blocked') {
      logger.warn('[FCM] Permission blocked by user');
    } else if (error.code === 'messaging/unsupported-browser') {
      logger.warn('[FCM] Browser not supported');
    } else if (error.code === 'messaging/token-subscribe-failed') {
      logger.warn('[FCM] Token subscription failed, will retry');
      // Could implement retry logic here
    }

    return null;
  }
}

/**
 * Derive a stable, Firestore-safe device ID from an FCM token.
 * Uses the last 32 characters of the token (always alphanumeric).
 * @param {string} token
 * @returns {string}
 */
function getTokenId(token) {
  return token.slice(-32);
}

/**
 * Save FCM token to Firestore.
 * Writes into a `devices` map keyed by a stable token-derived ID so that
 * multiple devices per user are all tracked. Also keeps the legacy root
 * `token` field updated so old Cloud Function code continues to work.
 * @param {string} token - FCM token
 * @returns {Promise<void>}
 */
async function saveFCMToken(token) {
  const user = auth.currentUser;
  if (!user) {
    logger.warn('[FCM] Cannot save token - no authenticated user');
    return;
  }

  // Wait a moment for auth token to fully propagate
  // This helps avoid race conditions with Firestore rules
  await new Promise(resolve => setTimeout(resolve, 500));

  // Re-check user after delay (in case of logout during wait)
  if (!auth.currentUser) {
    logger.warn('[FCM] User logged out during token save');
    return;
  }

  try {
    const tokenRef = doc(db, COLLECTIONS.FCM_TOKENS, user.uid);
    const deviceId = getTokenId(token);

    logger.info('[FCM] Saving token for user:', user.uid);

    // Read first so we can merge the devices map without overwriting other devices
    const existing = await getDoc(tokenRef);
    const existingDevices = existing.exists() ? (existing.data().devices || {}) : {};

    // Build fresh device entry — explicitly omit invalidatedAt so a previously
    // server-invalidated token can be retried after the user re-enables push.
    const deviceEntry = {
      token,
      active: true,
      platform: getPlatform(),
      browser: getBrowser(),
      registeredAt: serverTimestamp(),
    };
    // Clear any lingering invalidatedAt from a previous rejection for this deviceId
    if (existingDevices[deviceId]?.invalidatedAt) {
      delete existingDevices[deviceId].invalidatedAt;
    }

    await setDoc(tokenRef, {
      // Legacy root fields — keep for backward-compat with any old server code
      token,
      userId: user.uid,
      active: true,
      updatedAt: serverTimestamp(),
      // Multi-device map: each entry is keyed by a stable device ID
      devices: {
        ...existingDevices,
        [deviceId]: deviceEntry,
      }
    }, { merge: true });

    logger.info('[FCM] Token saved to Firestore successfully', { deviceId });

  } catch (error) {
    logger.error('[FCM] Failed to save token:', error);
    console.error('[FCM] Token save error details:', {
      userId: user.uid,
      errorCode: error.code,
      errorMessage: error.message
    });
  }
}

/**
 * Delete FCM token (when user disables notifications)
 * Unregisters the push subscription with Firebase AND marks the specific
 * device entry inactive in Firestore so Cloud Functions stop sending to it.
 * @returns {Promise<void>}
 */
export async function deleteFCMToken() {
  const user = auth.currentUser;
  if (!user) return;

  try {
    // Capture the token before unregistering (we need it to derive the device ID)
    const tokenToDelete = currentToken;

    // Unregister the token with Firebase so the browser stops receiving pushes
    if (messaging) {
      try {
        await deleteToken(messaging);
        logger.info('[FCM] Token unregistered from Firebase');
      } catch (err) {
        // Non-fatal — token may already be invalid
        logger.warn('[FCM] Could not unregister token:', err.message);
      }
      // Reset messaging instance so the next requestFCMToken call re-initializes
      // cleanly. Firebase's getToken can return null or a stale token after
      // deleteToken if we reuse the same instance without re-registering the SW.
      messaging = null;
      messagingSwRegistration = null;
    }

    const tokenRef = doc(db, COLLECTIONS.FCM_TOKENS, user.uid);

    if (tokenToDelete) {
      // Mark the specific device inactive in the devices map
      const deviceId = getTokenId(tokenToDelete);
      await updateDoc(tokenRef, {
        [`devices.${deviceId}.active`]: false,
        [`devices.${deviceId}.deletedAt`]: serverTimestamp(),
        // Also mark legacy root field inactive for backward-compat
        active: false,
        deletedAt: serverTimestamp()
      });
    } else {
      // Fallback: mark root token inactive (no token in memory)
      await setDoc(tokenRef, {
        active: false,
        deletedAt: serverTimestamp()
      }, { merge: true });
    }

    currentToken = null;

  } catch (error) {
    logger.error('[FCM] Failed to delete token:', error);
  }
}

/**
 * Handle foreground notification (when app is open)
 * @param {Object} payload - FCM message payload
 */
function handleForegroundMessage(payload) {
  const data = payload.data || {};
  // Data-only messages carry title/body in data; fall back to notification field
  const body = data.body || payload.notification?.body;

  if (!body) {
    return;
  }

  // Suppress insight toasts for 60s after the user saves an entry — the
  // "Entry saved!" toast already gives feedback and the insight lands in the bell.
  // Guarded: WKWebView with ITP can throw on sessionStorage access.
  let savedAtRaw = '0';
  try { savedAtRaw = sessionStorage.getItem('clarity_last_entry_saved') || '0'; } catch { /* ignore */ }
  const savedAt = parseInt(savedAtRaw, 10);
  if (Date.now() - savedAt < 60000) {
    return;
  }

  // Show in-app notification instead of browser notification
  showNotification(body, {
    type: data.type || 'insight',
    duration: 4000,
    cta: 'View',
    icon: '💡',
    onClick: () => {
      analytics.trackEvent('notification_tapped', {
        notification_type: data.type || 'insight',
        has_entry_id: !!data.entryId,
      });
      // Persist tap to Firestore via Cloud Function (fire-and-forget)
      if (RECORD_TAP_URL) {
        const user = auth.currentUser;
        const tapPayload = { type: data.type || 'insight', entryId: data.entryId || null, channel: 'foreground', source: 'fcm_foreground' };
        (user ? user.getIdToken() : Promise.resolve(null))
          .then(token => fetch(RECORD_TAP_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify(tapPayload)
          }))
          .catch(() => {}); // never block navigation
      }
      // Dispatch custom event — _layout.jsx listens and calls navigate()
      // Works in both web (history API) and Capacitor native (capacitor:// scheme)
      if (data.entryId) {
        window.dispatchEvent(new CustomEvent('clarity-navigate', { detail: `/journal/entry/${data.entryId}` }));
      } else if (data.url) {
        window.dispatchEvent(new CustomEvent('clarity-navigate', { detail: data.url }));
      }
    }
  });

  // Track notification received
  trackNotificationReceived(payload);
}

/**
 * Track notification received (for analytics)
 * @param {Object} payload - FCM message payload
 */
function trackNotificationReceived(payload) {
  const user = auth.currentUser;
  if (!user) return;

  try {
    const data = payload.data || {};
    analytics.trackEvent('notification_received', {
      notification_type: data.type || 'insight',
      has_entry_id: !!data.entryId,
    });
  } catch (error) {
    logger.error('[FCM] Failed to track notification:', error);
  }
}

/**
 * Get platform information
 * @returns {string}
 */
function getPlatform() {
  const platform = navigator.platform || '';
  const userAgent = navigator.userAgent || '';

  if (/iPhone|iPad|iPod/.test(userAgent)) return 'iOS';
  if (/Android/.test(userAgent)) return 'Android';
  if (/Mac/.test(platform)) return 'macOS';
  if (/Win/.test(platform)) return 'Windows';
  if (/Linux/.test(platform)) return 'Linux';

  return 'Unknown';
}

/**
 * Get browser information
 * @returns {string}
 */
function getBrowser() {
  const userAgent = navigator.userAgent || '';

  if (/Chrome/.test(userAgent) && !/Edge/.test(userAgent)) return 'Chrome';
  if (/Safari/.test(userAgent) && !/Chrome/.test(userAgent)) return 'Safari';
  if (/Firefox/.test(userAgent)) return 'Firefox';
  if (/Edge/.test(userAgent)) return 'Edge';

  return 'Unknown';
}

/**
 * Force a full token refresh — deletes the existing push subscription and
 * registers a brand new one. Use on the 5-day rotation schedule to prevent
 * stale subscriptions from silently failing delivery.
 * @returns {Promise<string|null>} new FCM token or null on failure
 */
export async function forceRefreshFCMToken() {
  if (isNative()) return null;
  try {
    // Reset messaging so deleteToken + getToken cycle runs cleanly
    if (messaging) {
      try { await deleteToken(messaging); } catch (e) { /* ignore — token may already be invalid */ }
      messaging = null;
      messagingSwRegistration = null;
      currentToken = null;
    }
    logger.info('[FCM] Force-refreshing token (5-day rotation)');
    return await requestFCMToken();
  } catch (err) {
    logger.warn('[FCM] Force refresh failed:', err.message);
    return null;
  }
}

/**
 * Check if FCM is initialized
 * @returns {boolean}
 */
export function isFCMInitialized() {
  return messaging !== null;
}

/**
 * Get current FCM token
 * @returns {string|null}
 */
export function getCurrentToken() {
  return currentToken;
}

/**
 * Check if push notifications are supported
 * @returns {boolean}
 */
export function isPushNotificationSupported() {
  return (
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

export default {
  initializeFCM,
  requestFCMToken,
  deleteFCMToken,
  isFCMInitialized,
  getCurrentToken,
  isPushNotificationSupported
};
